import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, Calendar, Sun, Moon, MapPin, Package, Clock, Truck, Phone, User } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { formatINR, formatDate, formatSlotWindow } from "../../lib/utils.fmt";
import LoginRequired from "./LoginRequired";
import EmptyState, { LoadingState } from "../../components/EmptyState";
import { todayIST, tomorrowIST } from "../../lib/istDate";

const SLOT_ORDER = { morning: 0, afternoon: 1, evening: 2 };

/**
 * Customer-facing "My Next Delivery" page.
 *
 * Earlier this page surfaced just the first row from /deliveries/upcoming —
 * which broke for customers running multiple subscriptions in the same slot
 * (e.g. milk + paneer + bread on Tomorrow Morning). Now we group ALL the
 * deliveries that share the earliest (date, slot) tuple and stack them in a
 * single card. The rider's name + phone is surfaced when assigned so the
 * customer can call the courier directly.
 */
export default function NextDelivery() {
  const { user, tenant } = useAuth();
  const [deliveries, setDeliveries] = useState([]);
  const [addrs, setAddrs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const [{ data: d }, { data: a }] = await Promise.all([
          api.get("/api/customer/deliveries/upcoming"),
          api.get("/api/customer/addresses"),
        ]);
        setDeliveries(d.deliveries || []);
        setAddrs(a.addresses || []);
      } finally { setLoading(false); }
    })();
  }, [user]);

  // Find the earliest (date, slot) and bundle every delivery that matches.
  const bundle = useMemo(() => {
    if (!deliveries.length) return null;
    const sorted = [...deliveries].sort((a, b) => {
      if (a.delivery_date !== b.delivery_date) return a.delivery_date < b.delivery_date ? -1 : 1;
      return (SLOT_ORDER[a.slot] ?? 99) - (SLOT_ORDER[b.slot] ?? 99);
    });
    const head = sorted[0];
    const items = sorted.filter((x) => x.delivery_date === head.delivery_date && x.slot === head.slot);
    return { ...head, items };
  }, [deliveries]);

  const addr = useMemo(
    () => (bundle && addrs.find((a) => a.id === bundle.address_id)) || null,
    [bundle, addrs]
  );

  // The admin-configured window for this delivery's slot. Until now this
  // screen named the slot ("morning slot") but showed no times at all — so
  // tapping through from Home's "Tomorrow · Morning 6:00 AM – 10:00 AM"
  // landed on a screen that had dropped the very detail it promised.
  const slotWindow = useMemo(() => {
    const wins = (tenant?.delivery_windows && tenant.delivery_windows.length
      ? tenant.delivery_windows
      : tenant?.delivery_window ? [tenant.delivery_window] : []);
    const slot = (bundle?.slot || "").toLowerCase();
    return wins.find((w) => (w?.label || "").toLowerCase() === slot) || null;
  }, [tenant, bundle]);

  // Collapse same-product rows into one line — a customer with 5 separate
  // "Toned Fresh Milk" subscriptions sees ONE line with `5 × ₹32 · 5 subs`
  // instead of five identical rows. Tap navigates to /app/subscriptions
  // to manage each underlying sub individually.
  const groupedItems = useMemo(() => {
    if (!bundle?.items?.length) return [];
    const out = [];
    const idx = new Map();
    for (const it of bundle.items) {
      const key = it.product_id || it.product_name;
      if (idx.has(key)) {
        const g = out[idx.get(key)];
        g.quantity += (it.quantity || 0);
        g.amount += (it.amount || 0);
        g.subCount += 1;
      } else {
        idx.set(key, out.length);
        out.push({
          ...it,
          quantity: it.quantity || 0,
          amount: it.amount || 0,
          subCount: 1,
        });
      }
    }
    return out;
  }, [bundle]);

  if (!user) return <LoginRequired title="Sign in to see your next delivery"/>;
  if (loading) return <LoadingState fullHeight testId="next-delivery-loading" />;

  const SlotIcon = bundle?.slot === "evening" ? Moon : Sun;
  const isToday = bundle?.delivery_date === todayIST();
  const tomorrowISO = tomorrowIST();
  const dayLabel = isToday ? "Today" : (bundle?.delivery_date === tomorrowISO ? "Tomorrow" : "Coming up");

  const totalAmount = bundle?.items?.reduce((s, x) => s + (x.amount || 0), 0) || 0;
  const totalQty = bundle?.items?.reduce((s, x) => s + (x.quantity || 0), 0) || 0;

  return (
    <div className="page-enter pb-24 min-h-screen flex flex-col" data-testid="next-delivery-page">
      <header className="sticky top-0 z-30 bg-brand-cream/95 backdrop-blur pt-safe">
        <div className="px-4 sm:px-5 pt-5 pb-4 flex items-center gap-2">
          <Link to="/app" className="w-10 h-10 rounded-full bg-white border border-neutral-200 flex items-center justify-center" data-testid="back-btn">
            <ChevronLeft className="w-5 h-5"/>
          </Link>
          <h1 className="font-heading text-xl sm:text-2xl font-extrabold tracking-tight flex items-center gap-2">
            <Truck className="w-5 h-5 text-brand-leaf"/> My Next Delivery
          </h1>
        </div>
      </header>

      <main className="px-4 sm:px-5 flex-1 flex flex-col">
        {!bundle ? (
          <EmptyState
            icon={Calendar}
            title="No upcoming deliveries"
            description="Nothing scheduled in the next 7 days."
            action={
              <Link to="/app/products" className="inline-flex items-center px-5 h-11 rounded-2xl font-bold text-sm bg-brand-ink text-white" data-testid="shop-link">
                Browse products
              </Link>
            }
            fullHeight
            testId="no-next-delivery"
          />
        ) : (
          <div className="space-y-4">
            {/* Hero */}
            <div className="rounded-3xl bg-gradient-to-br from-brand-leaf to-brand-sky text-white p-6" data-testid="next-delivery-hero">
              <div className="text-xs uppercase tracking-wider font-bold text-white/80">{dayLabel}</div>
              <div className="font-heading text-3xl font-extrabold mt-1" data-testid="next-delivery-date">{formatDate(bundle.delivery_date)}</div>
              <div className="text-sm text-white/90 mt-1 inline-flex items-center gap-1.5">
                <SlotIcon className="w-3.5 h-3.5"/> <span className="capitalize">{bundle.slot}</span> slot
                {slotWindow && (
                  <>
                    <span className="opacity-50">·</span>
                    <span data-testid="next-delivery-window">{formatSlotWindow(slotWindow)}</span>
                  </>
                )}
                <span className="opacity-50">·</span>
                <span className="font-extrabold uppercase tracking-wider text-[10px]" data-testid="next-delivery-status">{bundle.status?.replace("_", " ")}</span>
              </div>
            </div>

            {/* Rider — only when a courier has been stamped on this delivery */}
            {bundle.rider_name && (
              <div className="bg-white rounded-3xl border border-neutral-200 p-5" data-testid="next-delivery-rider">
                <div className="text-xs uppercase tracking-wider font-bold text-neutral-500 mb-2 inline-flex items-center gap-1.5">
                  <Truck className="w-3.5 h-3.5"/> Your rider
                </div>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-12 h-12 rounded-2xl bg-brand-leaf/10 text-brand-leaf flex items-center justify-center font-heading font-extrabold shrink-0">
                      {bundle.rider_name[0]?.toUpperCase() || <User className="w-5 h-5"/>}
                    </div>
                    <div className="min-w-0">
                      <div className="font-bold truncate" data-testid="rider-name">{bundle.rider_name}</div>
                      {bundle.rider_phone && <div className="text-sm text-neutral-500 font-mono">{bundle.rider_phone}</div>}
                    </div>
                  </div>
                  {bundle.rider_phone && (
                    <a href={`tel:${bundle.rider_phone}`}
                       className="h-11 px-4 rounded-2xl bg-brand-leaf text-white font-bold text-sm inline-flex items-center gap-1.5"
                       data-testid="call-rider">
                      <Phone className="w-4 h-4"/> Call
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Items — multi-product bundle */}
            <div className="bg-white rounded-3xl border border-neutral-200 p-5" data-testid="next-delivery-items">
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs uppercase tracking-wider font-bold text-neutral-500 inline-flex items-center gap-1.5">
                  <Package className="w-3.5 h-3.5"/> {groupedItems.length} item{groupedItems.length === 1 ? "" : "s"} · {totalQty} unit{totalQty === 1 ? "" : "s"}
                </div>
                <div className="font-mono font-extrabold text-lg" data-testid="bundle-total">{formatINR(totalAmount)}</div>
              </div>
              <div className="space-y-2">
                {groupedItems.map((it) => (
                  <div key={it.id} className="flex items-center justify-between gap-2 py-2 border-t border-neutral-100 first:border-t-0" data-testid={`bundle-item-${it.id}`}>
                    <div className="min-w-0">
                      <div className="font-semibold truncate">{it.product_name}</div>
                      <div className="text-xs text-neutral-500">
                        Qty {it.quantity}
                        {it.subCount > 1 ? ` · ${it.subCount} subs` : (it.order_type === "one_time" ? " · One-time" : " · Subscription")}
                      </div>
                    </div>
                    <div className="font-mono font-bold text-sm">{formatINR(it.amount)}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Address */}
            <div className="bg-white rounded-3xl border border-neutral-200 p-5" data-testid="next-delivery-address">
              <div className="text-xs uppercase tracking-wider font-bold text-neutral-500 mb-2 inline-flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5"/> Delivery to
              </div>
              {addr ? (
                <>
                  <div className="font-bold text-sm">{addr.label}</div>
                  <div className="text-sm text-neutral-700">
                    {addr.line1}{addr.line2 ? `, ${addr.line2}` : ""}
                  </div>
                  <div className="text-sm text-neutral-500">{addr.city}{addr.pincode ? ` · ${addr.pincode}` : ""}</div>
                </>
              ) : (
                <div className="text-sm text-neutral-500">Address details unavailable</div>
              )}
            </div>

            {bundle.editable && (
              <div className="rounded-2xl bg-brand-cream p-3 text-xs text-neutral-700 inline-flex items-center gap-2" data-testid="editable-note">
                <Clock className="w-3.5 h-3.5"/> You can still edit this delivery before the cutoff.
              </div>
            )}

            <div className="flex gap-2">
              <Link to="/app/subscriptions" className="flex-1 h-12 rounded-2xl bg-white border border-neutral-200 text-neutral-800 font-extrabold inline-flex items-center justify-center" data-testid="manage-subs-link">
                Manage subscriptions
              </Link>
              <Link to="/app/orders" className="flex-1 h-12 rounded-2xl bg-brand-ink text-white font-extrabold inline-flex items-center justify-center" data-testid="all-orders-link">
                All orders
              </Link>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
